import * as React from 'react';
import {
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {useSnackbar} from 'notistack';

import {
  ERROR,
  REQUEST_CANCELED,
  MASS_SEARCH_TOO_MANY_NUMBERS,
  MASS_SEARCH_MAX_NUMBERS,
  MIN_SEARCH_LENGTH,
  SEARCH_TOO_FEW_CHARACTERS,
  PAGE_SIZE,
  REGEX_MASSSEARCH_PATTERN,
  STRUCTURE_ID_MEDIENEINGANG,
  ABORT_SEARCH_SILENT,
} from '../constants';
import {useAuth} from './auth';
import {refreshBatch, search as searchRequest} from '../api';
import {
  SearchResponseDto,
  SearchResult,
  SearchAssetInformationDto,
  SearchFilterRequestDto,
  SearchValues,
  SearchContextValue,
  SEARCH_VALUES_SESSION_STORAGE_KEY,
} from '../models';
import {isPresent, overwriteArray} from '../utils/utils';

const initialResult: SearchResult = {
  page: 0,
  totalElements: 0,
  totalPages: 0,
  content: [],
};

export const SearchContext = React.createContext<SearchContextValue>({
  text: '',
  sort: '',
  filters: undefined,
  result: {page: 0, totalElements: 0, totalPages: 0, content: []},
  searching: false,
  refreshing: false,
  changeValues: () => {},
  hasMore: true,
  firstSearch: true,
  resetSearch: () => {},
  abortSearch: () => {},
  reload: () => {},
  refreshVisibleAssets: () => {},
  registerVisibleRange: () => {},
  userGroups: [],
  structureId: '',
  firstPage: true,
  waitUntilComplete: () => Promise.resolve(),
  loadSessionSearchValues: () => undefined,
});

export const useSearch = () => useContext(SearchContext);

interface SearchProviderProps extends PropsWithChildren {
  isolated?: boolean;
}

export const SearchProvider: React.FC<SearchProviderProps> = ({
  children,
  isolated = false,
}) => {
  const [structureId, setStructureId] = useState<string | undefined>();
  const [text, setText] = useState<string>('');
  const [sort, setSort] = useState<string>('');
  const [filters, setFilters] = useState<SearchFilterRequestDto | undefined>();
  const [result, setResult] = useState<SearchResult>(initialResult);
  const [searching, setSearching] = useState<boolean>(false);
  const [initial, setInitial] = useState<boolean>(true);
  const [firstSearch, setFirstSearch] = useState<boolean>(true);
  const [newSearch, setNewSearch] = useState<boolean>(false);
  const [page, setPage] = useState<number>(0);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [refreshing, setRefreshing] = useState<boolean>(false);
  const {enqueueSnackbar} = useSnackbar();
  const {token, forceLogout} = useAuth();
  // Callback-Ref für visible range (wird von ItemViewGrid/Table registriert)
  const visibleRangeCallbackRef = useRef<
    (() => {start: number; end: number}) | null
  >(null);
  // AbortController für refresh requests
  const refreshControllerRef = useRef<AbortController | null>(null);
  const searchControllerRef = useRef<AbortController | null>(null);
  const itemsCompleteRef = useRef(true);
  const itemsCompleteWaitersRef = useRef<(() => void)[]>([]);

  const resetSearch = useCallback(() => {
    searchControllerRef.current?.abort();
    setSearching(false);
    setPage(0);
    setHasMore(true);
    setInitial(true);
    setFirstSearch(true);
    setFilters(undefined);
    setStructureId(undefined);
    setText('');
    setResult(initialResult);
    setSort('');
  }, []);

  const abortSearch = useCallback(() => {
    searchControllerRef.current?.abort(ABORT_SEARCH_SILENT);
    setSearching(false);
  }, []);

  const search = () => {
    setSearching(true);

    searchControllerRef.current?.abort();
    const newController = new AbortController();
    searchControllerRef.current = newController;
    const {signal} = newController;

    let data: SearchResponseDto;
    searchRequest(
      JSON.stringify({
        structureId: `${structureId ? structureId : ''}`,
        text: text.trim().split(REGEX_MASSSEARCH_PATTERN).join(';'),
        extendedSearch: filters,
      }),
      token,
      newController.signal,
      page,
      sort,
    )
      .then(res => {
        data = res.data;
        if (res.data.totalElements > 0) {
          const isNewSearch = page === 0;
          if (isNewSearch) {
            const updatedContent = Array(data.totalElements).fill(undefined);
            overwriteArray(updatedContent, data.content, 0);
            setResult({
              page: data.page,
              totalPages: data.totalPages,
              totalElements: data.totalElements,
              content: updatedContent,
            });
            setFirstSearch(false);
          } else {
            const contentSize = Math.max(
              /* At least previous size */
              result?.content.length,
              /* For size larger than 1001 */
              page * PAGE_SIZE + data.content.length,
            );
            const updatedContent = Array(contentSize).fill(undefined);
            overwriteArray(updatedContent, result?.content, 0);
            overwriteArray(updatedContent, data.content, page * PAGE_SIZE);
            setResult({
              page: data.page,
              totalPages: data.totalPages,
              totalElements: updatedContent.length,
              content: updatedContent,
            });
            setFirstSearch(false);
          }
        } else {
          setResult({
            page: data.page,
            totalPages: data.totalPages,
            totalElements: data.totalElements,
            content: [],
          });
          setFirstSearch(false);
        }
        if (res.data.content.length !== PAGE_SIZE) {
          setHasMore(false);
        }
        setSearching(false);
      })
      .catch(err => {
        if (err?.code === 'ERR_CANCELED') {
          if (signal.reason !== ABORT_SEARCH_SILENT) {
            enqueueSnackbar(REQUEST_CANCELED, {variant: 'info'});
          }
        } else {
          switch (err?.response?.status) {
            case 401:
              forceLogout();
              break;
            case 422:
              enqueueSnackbar(err.response.data, {variant: 'warning'});
              setResult(initialResult);
              break;
            default:
              enqueueSnackbar(ERROR, {variant: 'error'});
              setResult(initialResult);
          }
          setSearching(false);
        }
      });
  };
  /**
   * Registriert einen Callback, der die aktuell sichtbaren Indizes zurückgibt.
   * Wird von ItemViewGrid/ItemViewTable aufgerufen.
   */
  const registerVisibleRange = useCallback(
    (callback: () => {start: number; end: number}) => {
      visibleRangeCallbackRef.current = callback;
    },
    [],
  );

  /**
   * Aktualisiert nur die sichtbaren Assets durch einen Batch-Request ans Backend.
   */
  const refreshVisibleAssets = useCallback(
    async (visibleStartIndex: number, visibleEndIndex: number) => {
      if (!result || result.content.length === 0) return;

      // Extrahiere die IDs der sichtbaren Assets (max 75 wegen Backend-Validierung)
      const visibleAssets = result.content.slice(
        visibleStartIndex,
        visibleEndIndex,
      );
      const assetIds = visibleAssets
        .slice(0, 75)
        .map((asset: SearchAssetInformationDto | undefined) => asset?.id)
        .filter(isPresent);

      if (assetIds.length === 0) return;

      // Warnung wenn mehr als 75 sichtbare Items (sollte normalerweise nicht vorkommen)
      if (visibleAssets.length > 75) {
        console.warn(
          `[refreshVisibleAssets] ${visibleAssets.length} sichtbare Items, limitiert auf 75`,
        );
      }

      // Abbrechen des vorherigen Refresh-Requests falls noch aktiv
      if (refreshControllerRef.current) {
        refreshControllerRef.current.abort();
      }
      refreshControllerRef.current = new AbortController();

      setRefreshing(true);
      try {
        const response = await refreshBatch(
          assetIds,
          structureId,
          token,
          refreshControllerRef.current.signal,
        );
        const updatedAssets: (SearchAssetInformationDto | null)[] =
          response.data;

        // Partielles Update des result.content Arrays
        setResult(prevResult => {
          if (!prevResult.content.length) return prevResult;

          const newContent = [...prevResult.content];
          // Sammle die Indizes der gelöschten Artikel
          const indicesToRemove: number[] = [];

          updatedAssets.forEach((updatedAsset, index) => {
            const targetIndex = visibleStartIndex + index;
            if (targetIndex < newContent.length) {
              if (updatedAsset) {
                newContent[targetIndex] = updatedAsset;
              } else {
                // Artikel wurde gelöscht - Index merken zum Entfernen
                indicesToRemove.push(targetIndex);
              }
            }
          });

          // Entferne die Indizes von hinten nach vorne, um Verschiebungen zu vermeiden
          for (let i = indicesToRemove.length - 1; i >= 0; i--) {
            newContent.splice(indicesToRemove[i], 1);
          }

          return {
            ...prevResult,
            content: newContent,
          };
        });

        setRefreshing(false);
      } catch (err: unknown) {
        const error = err as {
          code?: string;
          response?: {status?: number; data?: string};
        };
        if (error?.code === 'ERR_CANCELED') {
          // Request wurde abgebrochen (z.B. durch Scroll) - kein Fehler anzeigen
          return;
        }

        switch (error?.response?.status) {
          case 401:
            forceLogout();
            break;
          default:
            enqueueSnackbar('Fehler beim Aktualisieren der Assets', {
              variant: 'error',
            });
        }
        setRefreshing(false);
      }
    },
    [result, token, enqueueSnackbar, forceLogout, structureId],
  );

  const reload = useCallback(() => {
    // Wenn ein visible range callback registriert ist, nur sichtbare Assets aktualisieren
    // Wenn man im persönlichen Medieneingang ist, dann neue Suche auslösen. Meistens ist die Aktion
    // dort das gesamte Leeren und wegen Backendlogik ist es auf diese Weise einfacher
    if (
      visibleRangeCallbackRef.current &&
      structureId !== STRUCTURE_ID_MEDIENEINGANG
    ) {
      const {start, end} = visibleRangeCallbackRef.current();
      refreshVisibleAssets(start, end).catch(err => {
        console.error('Fehler beim Refresh der Assets:', err);
      });
    } else {
      // Fallback: komplette Suche neu laden
      setNewSearch(true);
    }
  }, [refreshVisibleAssets, structureId]);

  const storeSessionSearchValues = useCallback(
    (values: SearchValues) => {
      if (isolated) return;
      try {
        if (typeof window !== 'undefined') {
          let existingSearchValues: SearchValues = {};
          const rawSearchValues: string | null = sessionStorage.getItem(
            SEARCH_VALUES_SESSION_STORAGE_KEY,
          );
          if (rawSearchValues) {
            const searchValues: SearchValues = JSON.parse(rawSearchValues);
            existingSearchValues = searchValues;
          }

          const newValues = {
            ...existingSearchValues,
            ...values,
          };

          if (
            isPresent(values.filters) ||
            (isPresent(values.text) && values.text.length > 0) ||
            (isPresent(values.structureId) && values.structureId.length > 0)
          ) {
            newValues.filters = values.filters;
            newValues.text = values.text;
            newValues.structureId = values.structureId;
          }

          sessionStorage.setItem(
            SEARCH_VALUES_SESSION_STORAGE_KEY,
            JSON.stringify(newValues),
          );
        }
      } catch {
        console.warn('[SearchContext] SSR oder sessionStorage nicht verfügbar');
      }
    },
    [isolated],
  );

  const changeValues = useCallback(
    (values: SearchValues) => {
      setNewSearch(true);
      if ('structureId' in values) {
        setStructureId(values.structureId);
        setFilters(undefined);
        setPage(0);
        setHasMore(true);
        setSort('');
      }
      if ('text' in values) {
        setText(values.text ?? '');
        setFilters(undefined);
        setPage(0);
        setHasMore(true);
        setResult(initialResult);
        setSort('');
      }
      if ('filters' in values) {
        setFilters(values.filters);
        setPage(0);
        setHasMore(true);
        setResult(initialResult);
        setSort('');
      }
      if ('nextPage' in values) {
        setPage(prevPage => prevPage + 1);
      }
      if ('page' in values && values.page !== undefined) {
        setPage(values.page);
      }

      if ('sort' in values) {
        if (values.sort === '') {
          setSort('');
        } else {
          setSort('&sort=' + values.sort);
        }
      }

      storeSessionSearchValues(values);
    },
    [storeSessionSearchValues],
  );

  const loadSessionSearchValues = useCallback((): SearchValues | undefined => {
    if (isolated) return undefined;
    let rawSearchValues: string | null = null;
    try {
      if (typeof window !== 'undefined') {
        rawSearchValues = sessionStorage.getItem(
          SEARCH_VALUES_SESSION_STORAGE_KEY,
        );
      }
    } catch {
      console.warn('[SearchContext] SSR oder sessionStorage nicht verfügbar');
    }

    try {
      if (rawSearchValues) {
        const searchValues: SearchValues = JSON.parse(rawSearchValues);
        searchValues.page = 0;

        abortSearch();
        changeValues(searchValues);
        return searchValues;
      }
    } catch {
      return undefined;
    }
    return undefined;
  }, [changeValues, isolated, abortSearch]);

  // Search effect with intentionally limited dependencies
  useEffect(() => {
    setNewSearch(false);

    if (initial) {
      setInitial(false);
      return;
    }

    if (searching) return;
    const termsCount = text.split(/[,;\s/]+/).length;
    const textLength = text.trim().length;
    if (termsCount > MASS_SEARCH_MAX_NUMBERS) {
      if (newSearch) {
        enqueueSnackbar(
          MASS_SEARCH_TOO_MANY_NUMBERS.replace(
            '{}',
            MASS_SEARCH_MAX_NUMBERS.toString(),
          ),
          {variant: 'warning'},
        );
      }
    } else if (textLength > 0 && textLength < MIN_SEARCH_LENGTH) {
      if (newSearch) {
        enqueueSnackbar(
          SEARCH_TOO_FEW_CHARACTERS.replace('{}', MIN_SEARCH_LENGTH.toString()),
          {variant: 'warning'},
        );
      }
    } else {
      search();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, structureId, text, sort, newSearch]);

  useEffect(() => {
    const firstMissingItem = result.content.findIndex(item => !item);
    const lastItem = result.content.findLastIndex(item =>
      isPresent<SearchAssetInformationDto>(item),
    );
    itemsCompleteRef.current =
      firstMissingItem === -1 || firstMissingItem > lastItem;

    if (itemsCompleteRef.current) {
      itemsCompleteWaitersRef.current.forEach(resolve => resolve());
      itemsCompleteWaitersRef.current = [];
    }
  }, [result]);

  const waitUntilComplete = useCallback(() => {
    return new Promise<void>(resolve => {
      if (itemsCompleteRef.current) {
        resolve();
      } else {
        itemsCompleteWaitersRef.current.push(resolve);
      }
    });
  }, []);

  const value = useMemo(
    () => ({
      structureId,
      searching,
      refreshing,
      text,
      filters,
      sort,
      result,
      hasMore,
      firstPage: page === 0,
      firstSearch,
      changeValues,
      resetSearch,
      abortSearch,
      reload,
      refreshVisibleAssets,
      registerVisibleRange,
      waitUntilComplete,
      loadSessionSearchValues,
    }),
    [
      structureId,
      filters,
      sort,
      searching,
      refreshing,
      text,
      result,
      hasMore,
      firstSearch,
      changeValues,
      resetSearch,
      abortSearch,
      reload,
      refreshVisibleAssets,
      registerVisibleRange,
      page,
      waitUntilComplete,
      loadSessionSearchValues,
    ],
  );
  return (
    <SearchContext.Provider value={value}>{children}</SearchContext.Provider>
  );
};
